import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {provideRouter, Router, withComponentInputBinding} from '@angular/router';
import {routes} from './app.routes';
import {en_US, fi_FI, NZ_DATE_LOCALE, NZ_I18N} from 'ng-zorro-antd/i18n';
import {enUS, fi} from 'date-fns/locale';
import {FormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {NoopAnimationsModule, provideAnimations} from '@angular/platform-browser/animations';
import {provideTranslateService, TranslateLoader} from '@ngx-translate/core';
import {syncTranslateLoaderFactory} from './core/services/translate-require-loader.service';
import {LocaleService, localeServiceProviders} from './core/services/locale.service';
import {AuthInterceptor} from './core/auth.interceptor';
import {AuthService} from './core/services/auth.service';
import {Observable} from 'rxjs';
import * as Sentry from '@sentry/angular';
import {AUTH_SERVICE_TOKEN, LOCALE_SERVICE_TOKEN, NAVIGATION_SERVICE_TOKEN} from 'patja-ui';
import {NavigationService} from '../../../patja-admin/src/app/core/services/navigation.service';
import {TraceService} from '@sentry/angular';

function initializeCurrentUserFactory(authService: AuthService): () => void | Observable<unknown> {
  return () => authService.initialiseCurrentUser();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    {
      provide: NZ_I18N,
      useFactory: () => {
        const localeService = inject(LocaleService);
        const currentLocale = localeService.getLocale();
        return currentLocale === 'fi' ? fi_FI : en_US;
      },
      deps: [LocaleService],
    },
    importProvidersFrom(FormsModule),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(NoopAnimationsModule),
    provideAnimations(),
    localeServiceProviders(),
    provideAppInitializer(() => initializeCurrentUserFactory(inject(AuthService))()),
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: syncTranslateLoaderFactory,
      },
      defaultLanguage: 'en',
    }),
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: LOCALE_SERVICE_TOKEN,
      useExisting: LocaleService,
    },
    {
      provide: NAVIGATION_SERVICE_TOKEN,
      useExisting: NavigationService,
    },
    {
      provide: AUTH_SERVICE_TOKEN,
      useExisting: AuthService,
    },
    {
      provide: NZ_DATE_LOCALE,
      useFactory: () => {
        const localeService = inject(LocaleService);
        const currentLocale = localeService.getLocale();
        return currentLocale === 'fi' ? fi : enUS;
      },
      deps: [LocaleService],
    },
  ],
};
